import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Phone, ExternalLink } from 'lucide-react';
import {
  SUPPORT_DIRECTORY, FALLBACK_ENTRY, UNIVERSAL_MEETINGS,
  findCountry, telHref,
} from '../data/supportDirectory';
import { authFetch } from '../lib/authFetch';

/**
 * Public "Get help now" page.  No auth gate.
 *
 * Country resolution priority (Tudor Fix H2, 1 Oct 2026):
 *   1. Logged-in user's saved country via /api/support-directory/me.
 *   2. localStorage `user_country_code` (older manual override).
 *   3. navigator.language region hint.
 *   4. Australia (product default).
 */
export default function GetHelpPage() {
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const initialCountry = useMemo(() => {
    try {
      const saved = localStorage.getItem('user_country_code');
      if (saved && findCountry(saved)) return saved.toUpperCase();
    } catch (_) { /* private-mode */ }
    // No navigator-locale fallback: a user whose browser is set to en-GB but
    // whose account is Australia must NOT see UK numbers.  If we have no
    // saved country, render the page in a neutral state and let the signed-
    // in effect below (or the user) pick the right one.
    return null;
  }, []);
  const [countryCode, setCountryCode] = useState(initialCountry);
  const [showAll, setShowAll] = useState(false);
  const country = countryCode ? (findCountry(countryCode) || FALLBACK_ENTRY) : FALLBACK_ENTRY;

  // Persist the chosen country so the next page load uses it.
  useEffect(() => {
    if (!countryCode) return;
    try { localStorage.setItem('user_country_code', countryCode); } catch (_) { /* private mode */ }
  }, [countryCode]);

  useEffect(() => { document.title = 'Get help now: Axiom Recovery'; }, []);

  // Signed-in override: pull the user's saved country and use that if set.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${apiBase}/api/support-directory/me`);
        if (!r.ok) return;
        const d = await r.json();
        const cc = (d.country_code || '').toString().toUpperCase();
        if (!cancelled && cc && findCountry(cc)) {
          setCountryCode(cc);
        }
      } catch (_) { /* not signed in or offline: keep the guess */ }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="get-help-page">
      <header className="bg-white border-b border-zinc-200 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)}
                className="p-1 rounded hover:bg-zinc-100"
                data-testid="get-help-back-btn"
                aria-label="Back">
          <ChevronLeft className="w-5 h-5 text-zinc-600" />
        </button>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-600">Get help now</div>
          <h1 className="text-base font-bold text-zinc-900">Support services</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4">
        <div className="rounded-2xl bg-white border border-zinc-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              {country.country_name}
            </div>
            <select
              className="text-xs border border-zinc-200 rounded-md px-2 py-1"
              value={countryCode || ''}
              onChange={(e) => setCountryCode(e.target.value || null)}
              data-testid="get-help-country-select"
            >
              {!countryCode && <option value="">Choose your country</option>}
              {SUPPORT_DIRECTORY.map(c => (
                <option key={c.country_code} value={c.country_code}>{c.country_name}</option>
              ))}
              <option value="FALLBACK">Any other country</option>
            </select>
          </div>

          {country.emergency_number && (
            <a href={telHref(country.emergency_number)}
               className="flex items-center gap-2 p-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg mb-3 font-bold"
               data-testid="get-help-emergency">
              <Phone className="w-4 h-4" /> Emergency · {country.emergency_number}
            </a>
          )}

          <ul className="space-y-2">
            {(country.crisis_lines || []).map((line, i) => (
              <li key={i} className="border border-zinc-200 rounded-lg p-3">
                <div className="text-sm font-bold text-zinc-900">{line.name}</div>
                {line.number && (
                  <a href={telHref(line.number)}
                     className="text-sm text-rose-700 underline block mt-1"
                     data-testid={`get-help-crisis-${i}`}>
                    {line.number}
                  </a>
                )}
                {line.url && !line.number && (
                  <a href={line.url} target="_blank" rel="noopener noreferrer"
                     className="text-sm text-rose-700 underline flex items-center gap-1 mt-1"
                     data-testid={`get-help-crisis-${i}`}>
                    {line.url.replace('https://', '')} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {(line.hours || line.notes) && (
                  <div className="text-xs text-zinc-500 mt-1">
                    {line.hours}{line.hours && line.notes ? ' · ' : ''}{line.notes}
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div className="mt-4 border-t border-zinc-100 pt-3 text-sm text-zinc-700 space-y-1">
            {UNIVERSAL_MEETINGS.map((m, i) => (
              <div key={i}>
                <a className="underline font-bold" href={m.url} target="_blank" rel="noopener noreferrer">
                  {m.name}
                </a> · {m.description}
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => setShowAll(v => !v)}
          className="w-full text-sm text-zinc-700 hover:text-zinc-900 underline mb-4"
          data-testid="get-help-see-all-btn"
        >
          {showAll ? 'Hide' : 'See all countries'}
        </button>

        {showAll && (
          <div className="rounded-2xl bg-white border border-zinc-200 p-4 space-y-3"
               data-testid="get-help-all-countries">
            {SUPPORT_DIRECTORY.map(c => (
              <div key={c.country_code} className="border-b border-zinc-100 pb-3 last:border-0">
                <div className="text-sm font-bold text-zinc-900 mb-1">{c.country_name}</div>
                {c.emergency_number && (
                  <div className="text-xs text-zinc-700">
                    Emergency: <a className="underline" href={telHref(c.emergency_number)}>{c.emergency_number}</a>
                  </div>
                )}
                {(c.crisis_lines || []).map((l, i) => (
                  <div key={i} className="text-xs text-zinc-700">
                    {l.name}:{' '}
                    {l.number
                      ? <a className="underline" href={telHref(l.number)}>{l.number}</a>
                      : (l.url && <a className="underline" href={l.url} target="_blank" rel="noopener noreferrer">{l.url.replace('https://', '')}</a>)}
                    {l.hours && <span className="text-zinc-500"> · {l.hours}</span>}
                  </div>
                ))}
              </div>
            ))}
            <div className="text-xs text-zinc-500 pt-2">
              Not listed? Find a crisis line in your country at{' '}
              <a href="https://findahelpline.com" target="_blank" rel="noopener noreferrer" className="underline">findahelpline.com</a>.
            </div>
          </div>
        )}

        <div className="text-[11px] text-zinc-500 leading-relaxed mt-6 space-y-2">
          <p>Axiom Recovery Programme is not a crisis service and does not give medical advice.</p>
          <p>If you are in danger, contact emergency services.</p>
        </div>
      </main>
    </div>
  );
}
